export const PENDING_ACK_KEY = 'spanish-ack-pending';

/** A signed-out acceptance (login/registration page) held in this tab until the borrower's application is known. */
export interface PendingAcknowledgment {
  version: string;
  acceptedAt: number;
  /** The lender portal it was accepted on ('' when the page resolved the lender by its domain). */
  companyGuid: string;
}

/** How long a signed-out acceptance waits for the sign-in that follows it. */
export const PENDING_ACK_TTL_MS = 30 * 60 * 1000;

/**
 * A signed-out acceptance only counts for the sign-in that follows it on the same lender's portal, shortly
 * after — never for someone who later signs in on a shared tab (LLS-886 §5 evidence).
 */
export function usablePending(pending: PendingAcknowledgment | null, companyGuid: string | null | undefined, now: number): PendingAcknowledgment | null {
  if (!pending || typeof pending.version !== 'string' || typeof pending.acceptedAt !== 'number' || typeof pending.companyGuid !== 'string') {
    return null;
  }
  const otherLender = pending.companyGuid !== '' && pending.companyGuid !== (companyGuid ?? '');
  if (otherLender || now - pending.acceptedAt > PENDING_ACK_TTL_MS || pending.acceptedAt > now) {
    return null;
  }
  return pending;
}

/** The Spanish acknowledgment a borrower must accept (LLS-886 §5), as resolved by the Api. */
export interface SpanishAcknowledgmentContent {
  /** The language statement the borrower must check. */
  statementHtml: string;
  /** Note shown under it. */
  noteHtml: string;
  version: string;
}

/**
 * LLS-886 §5 gate: signed in + Spanish + the server says this borrower still needs the current version →
 * save a pending acceptance of that same version, else prompt. Signed-out pages never prompt passively
 * (only an explicit switch to ES does), so link/token pages stay exempt.
 */
export function decideGate(input: {
  lang: string;
  signedIn: boolean;
  status: { required: boolean; version: string | null } | null;
  pending: PendingAcknowledgment | null;
}): 'none' | 'save-pending' | 'prompt' {
  if (input.lang !== 'es' || !input.signedIn || !input.status?.required || !input.status.version) {
    return 'none';
  }
  return input.pending?.version === input.status.version ? 'save-pending' : 'prompt';
}

/** Applied-for co-borrowers that still need an acknowledgment recorded on their behalf. */
export function missingOnBehalf(appliedFor: number[], acknowledged: number[]): number[] {
  return appliedFor.filter(id => !acknowledged.includes(id));
}

/** Points the lender's {{TermsUrl}} / {{PrivacyUrl}} link tokens at the borrower app's Terms/Privacy page. */
export function fillLinkTokens(html: string, legalHref: string): string {
  return html.replace(/\{\{\s*(TermsUrl|PrivacyUrl)\s*\}\}/gi, legalHref);
}

/**
 * Contact ids (dbo.Borrower ids) of the co-borrowers the signed-in primary applies for in the wizard — the
 * same borrowers e-consent is recorded for. Empty in the co-apply flow, where each borrower acknowledges
 * for themselves.
 */
export function appliedForCoBorrowerIds(
  borrowers: { borrowerId: number; contactId: number }[],
  loggedInContactId: number,
  borrowerSettings: Map<number, boolean>,
  inCoApplyFlow: boolean,
): number[] {
  if (inCoApplyFlow) {
    return [];
  }
  return borrowers
    .filter(b => b.contactId !== loggedInContactId && borrowerSettings.get(b.borrowerId) === true)
    .map(b => b.contactId);
}
